import React, { useState, useRef, useMemo } from 'react'

interface VirtualizedListProps<T> {
    items: T[]
    containerHeight: number
    renderItem: (item: T, index: number) => React.ReactNode
    getItemHeight: (item: T) => number
    getItemKey?: (item: T, index: number) => React.Key
    overscan?: number
}

export function VirtualizedList<T>({
    items,
    containerHeight,
    renderItem,
    getItemHeight,
    getItemKey,
    overscan = 5,
}: VirtualizedListProps<T>) {
    const [scrollTop, setScrollTop] = useState(0)
    const scrollElementRef = useRef<HTMLDivElement>(null)

    const totalHeight = useMemo(
        () => items.reduce((sum, item) => sum + getItemHeight(item), 0),
        [items, getItemHeight]
    )

    const { visibleItems, offsetY } = useMemo(() => {
        let currentOffset = 0
        let startIndex = 0
        let endIndex = items.length - 1

        // Find start index
        for (let i = 0; i < items.length; i++) {
            const height = getItemHeight(items[i])
            if (currentOffset + height > scrollTop) {
                startIndex = Math.max(0, i - overscan)
                break
            }
            currentOffset += height
        }

        // Calculate offset for start index
        let calculatedOffset = 0
        for (let i = 0; i < startIndex; i++) {
            calculatedOffset += getItemHeight(items[i])
        }

        // Find end index
        let visibleHeight = 0
        for (let i = startIndex; i < items.length; i++) {
            visibleHeight += getItemHeight(items[i])
            if (visibleHeight > containerHeight * 2) {
                endIndex = Math.min(items.length - 1, i + overscan)
                break
            }
        }

        return {
            visibleItems: items
                .slice(startIndex, endIndex + 1)
                .map((item, index) => ({
                    item,
                    index: startIndex + index,
                })),
            offsetY: calculatedOffset,
        }
    }, [items, scrollTop, containerHeight, overscan, getItemHeight])

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop)
    }

    return (
        <div
            data-testid="virtualized-list"
            ref={scrollElementRef}
            onScroll={handleScroll}
            style={{
                height: containerHeight,
                overflow: 'auto',
            }}
        >
            <div
                style={{ height: totalHeight, position: 'relative' }}
                data-testid="virtualized-list-scroll-container"
            >
                <div
                    style={{
                        transform: `translateY(${String(offsetY)}px)`,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                    }}
                >
                    {visibleItems.map(({ item, index }) => (
                        <div
                            key={getItemKey ? getItemKey(item, index) : index}
                            style={{
                                height: getItemHeight(item),
                            }}
                        >
                            {renderItem(item, index)}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
